import type { FollowUpSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

/**
 * Conversation-assistant-local metadata layered onto a shared ConversationAction.
 * Used by opened insights today, but kept generic so chat code does not branch
 * on insight-specific action types.
 */
type ConversationAssistantActionMetadata = {
	followUps?: string[];
	presentation?: {
		hideActionHeader?: boolean;
		suppressPromptLibrary?: boolean;
		centerInFullScreen?: boolean;
	};
	seededConversation?: {
		name?: string;
		followUpSource?: FollowUpSource;
		tags?: string[];
		skipNextFetch?: boolean;
		onCreated?: (conversationId: string) => void;
	};
	onDismiss?: () => void;
};

export type ConversationActionWithAssistantMetadata = ConversationAction &
	ConversationAssistantActionMetadata;

const getConversationActionWithAssistantMetadata = (
	action: ConversationAction | undefined,
): ConversationActionWithAssistantMetadata | undefined =>
	action as ConversationActionWithAssistantMetadata | undefined;

export const getActionFollowUps = (action: ConversationAction | undefined): string[] =>
	getConversationActionWithAssistantMetadata(action)?.followUps ?? [];

export const getActionPresentation = (
	action: ConversationAction | undefined,
): NonNullable<ConversationActionWithAssistantMetadata['presentation']> =>
	getConversationActionWithAssistantMetadata(action)?.presentation ?? {};

export const getActionSeededConversationOptions = (
	action: ConversationAction | undefined,
): ConversationActionWithAssistantMetadata['seededConversation'] =>
	getConversationActionWithAssistantMetadata(action)?.seededConversation;

export const getActionOnDismiss = (
	action: ConversationAction | undefined,
): ConversationActionWithAssistantMetadata['onDismiss'] =>
	getConversationActionWithAssistantMetadata(action)?.onDismiss;
